import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { SeasonInfo } from '@shared/types.ts';
import { api } from '@/lib/api.ts';
import { keys, useCollection, useRequestDetails } from '@/lib/queries.ts';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { useBackdrop } from '@/stores/backdrop.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { Strip } from '@/ui/Strip.tsx';

/**
 * SOMETHING THE HOUSE DOES NOT HAVE YET, on a screen of its own.
 *
 * This replaces a modal, and the reason is the reason the television's
 * version stopped being one: a title you can ask for should look like a
 * different KIND of the same thing as a title you own, not a broken version
 * of it. The dialog had a thumbnail and a list of buttons; every library item
 * has a backdrop, a cast row and room to read. Deciding whether you want
 * something is exactly the moment that material earns its place.
 *
 * SEASONS ARE TOGGLES ON THE PAGE, not a second pane behind a button, and
 * NOTHING IS TICKED WHEN IT OPENS. That is Seerr's own behaviour and it is
 * the safe default: a screen that arrives with all nine seasons selected is
 * one mis-press from asking the house to download all nine.
 *
 * A ROUTE, not a piece of state on the search screen, so Back works, the page
 * survives a reload, and somebody can send the link to whoever actually knows
 * whether the 1994 cartoon is the one they meant.
 */
export function RequestPage() {
  const { mediaType, tmdbId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const kind = mediaType === 'tv' ? 'tv' : 'movie';
  const id = Number(tmdbId);

  const { data, isLoading, error } = useRequestDetails(kind, Number.isFinite(id) ? id : null);

  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  /* The ambient backdrop stands down while this screen draws its own, the
     same trade the library detail page makes. Two at once is muddy. */
  useEffect(() => {
    const { suppress } = useBackdrop.getState();
    suppress(true);
    return () => suppress(false);
  }, []);

  useEffect(() => pushKeyLayer((k: NavKey) => {
    if (k !== 'back') return false;
    navigate(-1);
    return true;
  }), [navigate]);

  const seasons: SeasonInfo[] = data?.seasons ?? [];
  // A season already here, on its way, or asked for is not one to ask again.
  const askable = seasons.filter((s) => s.state === 'none');
  const isSeries = kind === 'tv';

  const { data: series } = useCollection(data?.collection?.id ?? null);
  const seriesMissing = (series?.parts ?? []).filter((p) => p.state === 'none');

  const settled = async () => {
    // Availability has just changed in both places that show it.
    await qc.invalidateQueries({ queryKey: ['search'] });
    await qc.invalidateQueries({ queryKey: ['collection'] });
    void qc.invalidateQueries({ queryKey: keys.libraryHome });
  };

  async function ask(which?: number[]) {
    setBusy(true);
    setFailed(null);
    try {
      await api.request(id, kind, which);
      setDone(which?.length
        ? `Asked for ${which.length} season${which.length > 1 ? 's' : ''}.`
        : 'Asked for — you will see it here when it arrives.');
      setPicked([]);
      await settled();
    } catch (e) {
      setFailed((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /*
   * The whole film series in one press.
   *
   * The alternative is what Seerr's own interface saves you from: searching
   * nine times, confirming nine times, and giving up around the sixth — after
   * which somebody asks you to do it for them, which is the errand this
   * feature exists to stop landing on you.
   */
  async function askSeries() {
    if (!seriesMissing.length) return;
    setBusy(true);
    setFailed(null);
    try {
      const r = await api.requestCollection(seriesMissing.map((p) => p.tmdbId!).filter(Boolean));
      // Says what actually happened rather than assuming. One film can fail a
      // quota boundary while the other eight go through.
      setDone(r.failed
        ? `Asked for ${r.asked}, and ${r.failed} would not go through.`
        : `Asked for all ${r.asked}.`);
      await settled();
    } catch (e) {
      setFailed((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <div className="muted center">Loading…</div>;
  if (error || !data) {
    return (
      <div className="center">
        <div className="panel">
          <h1>Couldn’t look that up</h1>
          <p className="muted">{(error as Error)?.message ?? 'Nothing came back for that title.'}</p>
        </div>
      </div>
    );
  }

  const allPicked = askable.length > 0 && picked.length === askable.length;
  const toggle = (n: number) =>
    setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));

  return (
    <>
      {data.backdropUrl && (
        <div className="libe-backdrop" style={{ backgroundImage: `url(${data.backdropUrl})` }} />
      )}

      <div className="libe-detail">
        {data.posterUrl && <img src={data.posterUrl} alt="" className="libe-detail-poster" />}

        <div className="libe-detail-text">
          <h2 className="libe-heading">{data.title}</h2>

          <div className="libe-meta">
            {[
              data.year,
              kind === 'tv' ? 'Series' : 'Film',
              seasons.length ? `${seasons.length} seasons` : null,
              data.runtime ? `${data.runtime}m` : null,
              data.rating ? `★ ${data.rating.toFixed(1)}` : null,
            ].filter(Boolean).map((bit, i) => (
              <span key={i} className="libe-meta-bit">{bit}</span>
            ))}
            {data.genres.map((g) => <span key={g} className="libe-genre">{g}</span>)}
          </div>

          {data.overview && <p className="libe-overview">{data.overview}</p>}

          {done && <p className="req-said">{done}</p>}
          {failed && <p className="admin-error">{failed}</p>}

          {/* A FILM IS ONE PRESS. There is nothing to choose between. */}
          {!isSeries && !done && (
            <div className="libe-actions">
              <Focusable
                id="req-go"
                autoFocus
                className="btn primary libe-play"
                onSelect={() => { if (!busy) void ask(); }}
              >
                {busy ? 'Asking…' : data.state === 'none' ? 'Request this film' : 'Request again'}
              </Focusable>
            </div>
          )}
        </div>
      </div>

      {/*
        SEASONS, ON THE PAGE.
        
        Above the cast, because this is what somebody came to do and the cast
        is what they read while deciding. The master toggle sits with the rest
        rather than above them as a heading: it is one more choice, and making
        it look like a section title implied it was a label for what followed.
      */}
      {isSeries && askable.length > 0 && !done && (
        <section className="libe-row req-seasons">
          <h2 className="libe-title">Seasons</h2>
          <p className="muted req-hint">
            Nothing is selected to begin with. Tick what you want and ask for it.
          </p>

          <div className="req-season-list">
            <Focusable
              id="req-season-all"
              autoFocus
              className={`req-season${allPicked ? ' is-on' : ''}`}
              onSelect={() => setPicked(allPicked ? [] : askable.map((s) => s.seasonNumber))}
            >
              <span className="req-tick">{allPicked ? '✓' : ''}</span>
              <span className="req-season-name">All seasons</span>
              <span className="muted req-season-note">{askable.length} missing</span>
            </Focusable>

            {seasons.map((s) => {
              const here = s.state !== 'none';
              const on = picked.includes(s.seasonNumber);
              return (
                <Focusable
                  key={s.seasonNumber}
                  id={`req-season-${s.seasonNumber}`}
                  className={`req-season${on ? ' is-on' : ''}${here ? ' is-had' : ''}`}
                  onSelect={() => { if (!here) toggle(s.seasonNumber); }}
                >
                  <span className="req-tick">{on ? '✓' : ''}</span>
                  <span className="req-season-name">Season {s.seasonNumber}</span>
                  <span className="muted req-season-note">
                    {/* What it would cost, or why it is not on offer. */}
                    {here ? stateWord(s.state) : `${s.episodeCount} episodes`}
                  </span>
                </Focusable>
              );
            })}
          </div>

          <div className="libe-actions">
            {/* INERT UNTIL SOMETHING IS TICKED, and it says why rather than
                sitting there greyed out with no explanation. */}
            <Focusable
              id="req-seasons-go"
              className={`btn libe-play${picked.length ? ' primary' : ''}`}
              onSelect={() => { if (!busy && picked.length) void ask(picked); }}
            >
              {busy ? 'Asking…'
                : picked.length ? `Request ${picked.length} season${picked.length > 1 ? 's' : ''}`
                  : 'Select season(s)'}
            </Focusable>
          </div>
        </section>
      )}

      {isSeries && askable.length === 0 && !done && (
        <p className="muted center req-hint">
          {seasons.length
            ? 'Every season is already here or on its way.'
            : 'No seasons listed for this one.'}
        </p>
      )}

      {data.cast.length > 0 && (
        <section className="libe-row libe-cast">
          <h2 className="libe-title">Cast</h2>
          <Strip>
            {data.cast.map((p) => (
              <div key={`${p.name}-${p.role ?? ''}`} className="cast-card">
                <div className="cast-face">
                  {p.thumb
                    ? <img src={p.thumb} alt="" loading="lazy" />
                    : <span className="cast-initial">{p.name.slice(0, 1)}</span>}
                </div>
                <div className="cast-name">{p.name}</div>
                {p.role && <div className="cast-role muted">{p.role}</div>}
              </div>
            ))}
          </Strip>
        </section>
      )}

      {/*
        The series this film belongs to — Saw, Harry Potter. INLINE rather
        than behind a button, because it is the answer to a question somebody
        already has by the time they are on this screen, and every film in it
        is shown with where it stands so the button above is not a surprise.
      */}
      {!isSeries && data.collection && series && (
        <section className="libe-row">
          <h2 className="libe-title">{data.collection.name}</h2>
          <div className="req-series">
            {series.parts.map((p) => (
              <div key={p.key} className={`req-part${p.state === 'none' ? '' : ' is-had'}`}>
                <span className="req-part-name">
                  {p.title}{p.year && <span className="muted"> {p.year}</span>}
                </span>
                <span className="muted req-season-note">
                  {p.state === 'none' ? 'not here' : stateWord(p.state)}
                </span>
              </div>
            ))}
          </div>
          {seriesMissing.length > 0 && !done && (
            <div className="libe-actions">
              <Focusable
                id="req-series-all"
                className="btn libe-play"
                onSelect={() => { if (!busy) void askSeries(); }}
              >
                {busy ? 'Asking…' : `Request the ${seriesMissing.length} you don’t have`}
              </Focusable>
            </div>
          )}
        </section>
      )}
    </>
  );
}

/** Seerr's states, as a person would say them. */
function stateWord(state: SeasonInfo['state']): string {
  return state === 'available' ? 'here'
    : state === 'partial' ? 'partly here'
      : state === 'processing' ? 'downloading'
        : 'requested';
}
